<template>
    <Head title="Accept profile invitation" />
    <BaseLayout gap="lg" fill-height content-class="justify-center text-valence-grey-800">
        <Transition mode="out-in" name="fade">
            <LoadingStep v-if="isAccepting" :main-loading-message="`Opening ${formattedName}'s profile...`" :hide-messages="true" />
            <div v-else class="flex flex-col gap-6 rounded-2xl bg-white border border-valence-grey-300 shadow-sm p-6 md:p-10">
                <BasePageTitle :title="title">
                    <template #subtitle>
                        Accepting will let you view their Collaboration Profile and add them to My People, so Nadia can provide personalized coaching on working with them.
                    </template>
                </BasePageTitle>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
                <BaseButton theme="primary" class="self-center" @click="handleAccept">Accept and view</BaseButton>
            </div>
        </Transition>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import { capitalizeName } from "~vue/utils/stringUtils";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ShareInvitationAccepted } from "~vue/api/generated/collab-profiles";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import { computed, ref } from "vue";

const props = defineProps<{
    invitation_id: string;
    owner_name: string;
    target_user_uuid: string;
}>();

const isAccepting = ref<boolean>(false);
const error = ref<string | null>(null);

const formattedName = computed(() => (props.owner_name ? capitalizeName(props.owner_name) : ""));

const title = computed(() => (formattedName.value ? `${formattedName.value} shared their profile with you` : "Someone shared their profile with you"));

const { error: acceptError, execute: executeAccept } = useFetch(`/v1/shared-profile-invitations/${props.invitation_id}/accept`, { immediate: false })
    .post()
    .json<ShareInvitationAccepted>();

const handleAccept = async (): Promise<void> => {
    isAccepting.value = true;
    error.value = null;
    await executeAccept();
    if (acceptError.value) {
        error.value = "This invitation is no longer valid. Please contact the profile owner.";
        isAccepting.value = false;
        return;
    }
    // Replace history so the back button doesn't return to the confirm page
    router.visit(`/accounts/collaboration-profile/${props.target_user_uuid}`, { replace: true });
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
